import React from "react"

import {
    Avatar,
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    IconButton,
    MenuItem,
    Popover,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material"
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder"
import FavoriteIcon from "@mui/icons-material/Favorite"
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined"
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt"
import SmartToyIcon from "@mui/icons-material/SmartToy"
import { t, Trans } from "@lingui/macro"
import Icon from "@mdi/react"
import { mdiChevronDoubleRight } from "@mdi/js"

import {
    Asset,
    AssetStatus,
    AssetType,
    ContractType,
    Document,
    PaginatedResponse,
    PositionType,
    SessionResponse,
} from "models"
import { PhotoList } from "components/asset/asset-details/asset-photo/photoList"

export type EditableAssetMetric = "gla" | "height" | "shopWindows" | "bathrooms"

export interface EditableMetricState {
    value: string
    isEditing: boolean
}

interface AssetHeroSectionProps {
    userIsBrand: boolean
    likeAsset: boolean
    onToggleFavourite: () => void
    immobiliareLink: string
    aiStartLabel: string
    onStartAiAnalysis: () => void
    heroTitle: string
    asset: Asset
    sessionData: SessionResponse
    photoData: PaginatedResponse<Document> | null
    onUploadPhoto: (request: FormData) => void
    onDeletePhoto: (photoId: string) => void
    highlightChips: string[]
    assetTypeLabel: string
    zoneLabel: string
    locationLine: string
    priceLabel: string
    pricePerMqLabel: string
    formatNumber: (value?: number | null, fractionDigits?: number) => string
    bathroomsValue: number | null
    editableMetrics: Record<EditableAssetMetric, EditableMetricState>
    onMetricDoubleClick: (metric: EditableAssetMetric) => void
    onMetricChange: (metric: EditableAssetMetric, value: string) => void
    onMetricKeyDown: (metric: EditableAssetMetric, event: React.KeyboardEvent) => void
    onMetricBlur: (metric: EditableAssetMetric) => void
    editableStatus: { value: AssetStatus | ""; isEditing: boolean }
    editableAddress: { value: string; isEditing: boolean }
    editablePrice: { value: string; isEditing: boolean }
    editableAssetType: { value: AssetType | ""; isEditing: boolean }
    onStatusDoubleClick: () => void
    onStatusChange: (status: AssetStatus) => void
    onStatusKeyDown: (event: React.KeyboardEvent) => void
    onStatusBlur: () => void
    onAddressDoubleClick: () => void
    onAddressChange: (value: string) => void
    onAddressKeyDown: (event: React.KeyboardEvent) => void
    onAddressBlur: () => void
    onPriceDoubleClick: () => void
    onPriceChange: (value: string) => void
    onPriceKeyDown: (event: React.KeyboardEvent) => void
    onPriceBlur: () => void
    onAssetTypeDoubleClick: () => void
    onAssetTypeChange: (type: AssetType) => void
    onAssetTypeKeyDown: (event: React.KeyboardEvent) => void
    onAssetTypeBlur: () => void
    statusOptions: { value: AssetStatus; label: string }[]
    featureChips: string[]
    landlordLogo: string
    landlordName: string
    onOpenLandlordModal: () => void
    canAssociateLandlord: boolean
    onAssociateLandlord: () => void
    isQuickActionsOpen: boolean
    onOpenQuickActions: () => void
    onCloseQuickActions: () => void
    onQuickActionSendProposal: () => void
    onQuickActionOpenImmobiliare?: () => void
    onQuickActionManageFloors: () => void
    onQuickActionCreateReminder: () => void
    onQuickActionTriggerWorkflow: () => void
}

export const AssetHeroSection = ({
    userIsBrand,
    likeAsset,
    onToggleFavourite,
    immobiliareLink,
    aiStartLabel,
    onStartAiAnalysis,
    heroTitle,
    asset,
    sessionData,
    photoData,
    onUploadPhoto,
    onDeletePhoto,
    highlightChips,
    assetTypeLabel,
    zoneLabel,
    locationLine,
    priceLabel,
    pricePerMqLabel,
    formatNumber,
    bathroomsValue,
    editableMetrics,
    onMetricDoubleClick,
    onMetricChange,
    onMetricKeyDown,
    onMetricBlur,
    editableStatus,
    editableAddress,
    editablePrice,
    editableAssetType,
    onStatusDoubleClick,
    onStatusChange,
    onStatusKeyDown,
    onStatusBlur,
    onAddressDoubleClick,
    onAddressChange,
    onAddressKeyDown,
    onAddressBlur,
    onPriceDoubleClick,
    onPriceChange,
    onPriceKeyDown,
    onPriceBlur,
    onAssetTypeDoubleClick,
    onAssetTypeChange,
    onAssetTypeKeyDown,
    onAssetTypeBlur,
    statusOptions,
    featureChips,
    landlordLogo,
    landlordName,
    isQuickActionsOpen,
    onOpenQuickActions,
    onCloseQuickActions,
    onQuickActionSendProposal,
    onQuickActionOpenImmobiliare,
    onQuickActionManageFloors,
    onQuickActionCreateReminder,
    onQuickActionTriggerWorkflow,
    onOpenLandlordModal,
    canAssociateLandlord,
    onAssociateLandlord,
}: AssetHeroSectionProps) => {
    const assetTypeOptions = Object.values(AssetType) as AssetType[]
    const translateAssetType = (type: string | AssetType | "") =>
        type ? t({ id: `enums.asset-types.${type}`, message: String(type) }) : "-"
    const quickActionsButtonRef = React.useRef<HTMLButtonElement | null>(null)
    const handleQuickActionsClick = () => {
        if (isQuickActionsOpen) {
            onCloseQuickActions()
        } else {
            onOpenQuickActions()
        }
    }

    return (
        <Box sx={{ borderRadius: 2, overflow: "hidden", border: 1, borderColor: "divider", backgroundColor: "background.paper" }}>
            <Box
                sx={{
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    px: { xs: 2, md: 3 },
                    py: 2,
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: { xs: "flex-start", md: "center" },
                    gap: 2,
                }}
            >
                <Typography variant="h5" sx={{ fontWeight: 600, flex: 1 }}>
                    {heroTitle}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                    {userIsBrand && (
                        <Button
                            variant={likeAsset ? "contained" : "outlined"}
                            color={likeAsset ? "secondary" : "inherit"}
                            startIcon={likeAsset ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                            onClick={onToggleFavourite}
                            sx={{
                                textTransform: "none",
                                borderColor: "rgba(255,255,255,0.5)",
                                color: likeAsset ? "primary.contrastText" : "inherit",
                            }}
                        >
                            <Trans id="asset-details.save">Salva</Trans>
                        </Button>
                    )}
                    <Tooltip title={aiStartLabel}>
                        <IconButton
                            onClick={onStartAiAnalysis}
                            aria-label={aiStartLabel}
                            sx={{
                                border: "1px solid rgba(255,255,255,0.5)",
                                color: "inherit",
                            }}
                        >
                            <SmartToyIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="outlined"
                        color="inherit"
                        onClick={handleQuickActionsClick}
                        ref={quickActionsButtonRef}
                        startIcon={
                            <Icon
                                path={mdiChevronDoubleRight}
                                size={0.8}
                                color="currentColor"
                            />
                        }
                        sx={{
                            textTransform: "none",
                            borderColor: "rgba(255,255,255,0.5)",
                            color: "inherit",
                        }}
                    >
                        <Trans id={"dms-fast-actions"}>Azioni rapide</Trans>
                    </Button>
                </Stack>
            </Box>
            <Popover
                anchorEl={quickActionsButtonRef.current}
                open={isQuickActionsOpen}
                onClose={onCloseQuickActions}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                slotProps={{
                    paper: {
                        sx: {
                            p: 2,
                            width: 220,
                        },
                    },
                }}
            >
                <Stack spacing={2}>
                    <Box
                        sx={{ cursor: "pointer" }}
                        onClick={onQuickActionSendProposal}
                    >
                        <Trans id={"dms-fast-actions.sent-match"}>Invia proposta</Trans>
                    </Box>
                    {!!immobiliareLink && onQuickActionOpenImmobiliare && (
                        <Box
                            sx={{ cursor: "pointer" }}
                            onClick={onQuickActionOpenImmobiliare}
                        >
                            <Trans id={"dms-fast-actions.go-immobiliare"}>Vai a Immobiliare</Trans>
                        </Box>
                    )}
                    <Box
                        sx={{ cursor: "pointer" }}
                        onClick={onQuickActionManageFloors}
                    >
                        <Trans id={"dms-fast-actions.manage-floors"}>Gestisci piani</Trans>
                    </Box>
                    <Box
                        sx={{ cursor: "pointer" }}
                        onClick={onQuickActionCreateReminder}
                    >
                        <Trans id={"dms-fast-actions.reminder"}>Crea promemoria</Trans>
                    </Box>
                    <Box
                        sx={{ cursor: "pointer" }}
                        onClick={onQuickActionTriggerWorkflow}
                    >
                        <Trans id={"dms-fast-actions.workflow"}>Scatena workflow</Trans>
                    </Box>
                </Stack>
            </Popover>
            <Grid container spacing={3} sx={{ p: { xs: 2, md: 3 } }}>
                <Grid item xs={12} md={7}>
                    {photoData ? (
                        <PhotoList
                            asset={asset}
                            sessionData={sessionData}
                            photoData={photoData}
                            callbackUploadPhoto={onUploadPhoto}
                            deletePhoto={onDeletePhoto}
                            editableStatus={editableStatus}
                            onStatusDoubleClick={onStatusDoubleClick}
                            onStatusChange={onStatusChange}
                            onStatusKeyDown={onStatusKeyDown}
                            onStatusBlur={onStatusBlur}
                            statusOptions={statusOptions}
                        />
                    ) : (
                        <Typography color="text.secondary">
                            <Trans id="asset-details.gallery.empty">Nessuna immagine disponibile.</Trans>
                        </Typography>
                    )}
                </Grid>
                <Grid item xs={12} md={5}>
                    <Stack spacing={2}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                            <Box
                                onDoubleClick={onAssetTypeDoubleClick}
                                sx={{ cursor: editableAssetType.isEditing ? "text" : "pointer" }}
                            >
                                {editableAssetType.isEditing ? (
                                    <TextField
                                        size="small"
                                        select
                                        autoFocus
                                        value={editableAssetType.value}
                                        onChange={(event) => onAssetTypeChange(event.target.value as AssetType)}
                                        onKeyDown={onAssetTypeKeyDown}
                                        onBlur={onAssetTypeBlur}
                                        sx={{ minWidth: 180 }}
                                        SelectProps={{
                                            renderValue: (selected) => translateAssetType(selected as AssetType | ""),
                                        }}
                                    >
                                    {assetTypeOptions.map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {translateAssetType(type)}
                                        </MenuItem>
                                    ))}
                                    </TextField>
                                ) : (
                                    <Chip
                                        label={assetTypeLabel}
                                        size="small"
                                        sx={{
                                            backgroundColor: "info.light",
                                            color: "primary.main",
                                            fontWeight: 600,
                                        }}
                                    />
                                )}
                            </Box>
                            {highlightChips.map((label) => (
                                <Chip
                                    key={label}
                                    label={label}
                                    size="small"
                                    sx={{
                                        backgroundColor: "info.light",
                                        color: "primary.main",
                                        fontWeight: 600,
                                    }}
                                />
                            ))}
                        </Stack>
                        <Box>
                            <Typography variant="h6" fontWeight={600}>
                                {zoneLabel}
                            </Typography>
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                onDoubleClick={onAddressDoubleClick}
                                sx={{ cursor: editableAddress.isEditing ? "text" : "pointer" }}
                            >
                                <LocationOnOutlinedIcon fontSize="small" color="disabled" />
                                {editableAddress.isEditing ? (
                                    <TextField
                                        size="small"
                                        autoFocus
                                        value={editableAddress.value}
                                        onChange={(event) => onAddressChange(event.target.value)}
                                        onKeyDown={onAddressKeyDown}
                                        onBlur={onAddressBlur}
                                        fullWidth
                                        variant="standard"
                                    />
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        {locationLine || "-"}
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Box
                                sx={{
                                    textAlign: { xs: "left", md: "right" },
                                    width: "100%",
                                    cursor: editablePrice.isEditing ? "text" : "pointer",
                                }}
                                onDoubleClick={onPriceDoubleClick}
                            >
                                {editablePrice.isEditing ? (
                                    <TextField
                                        size="small"
                                        autoFocus
                                        value={editablePrice.value}
                                        onChange={(event) => onPriceChange(event.target.value)}
                                        onKeyDown={onPriceKeyDown}
                                        onBlur={onPriceBlur}
                                        InputProps={{ sx: { fontWeight: 700, fontSize: 24, textAlign: "right" } }}
                                        fullWidth
                                    />
                                ) : (
                                    <Typography variant="h5" fontWeight={700}>
                                        {priceLabel}
                                    </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                    {pricePerMqLabel}
                                </Typography>
                            </Box>
                        </Box>
                        <Divider />
                        <Grid container spacing={2} textAlign="center">
                            {[
                                {
                                    key: "gla" as EditableAssetMetric,
                                    label: <Trans id="asset-details.kpi.gla">mq totali</Trans>,
                                    value: asset?.characteristics?.gla,
                                },
                                {
                                    key: "height" as EditableAssetMetric,
                                    label: <Trans id="asset-details.kpi.height">mt altezza</Trans>,
                                    value: asset?.characteristics?.height,
                                    fractionDigits: 2,
                                },
                                {
                                    key: "shopWindows" as EditableAssetMetric,
                                    label: <Trans id="asset-details.kpi.windows">vetrine</Trans>,
                                    value: asset?.characteristics?.shopWindows,
                                },
                                {
                                    key: "bathrooms" as EditableAssetMetric,
                                    label: <Trans id="asset-details.kpi.bathrooms">bagni</Trans>,
                                    value: bathroomsValue,
                                },
                            ].map((metric) => {
                                const metricState = editableMetrics[metric.key]
                                return (
                                    <Grid
                                        item
                                        xs={6}
                                        sm={3}
                                        key={metric.key}
                                        onDoubleClick={() => onMetricDoubleClick(metric.key)}
                                        sx={{ cursor: metricState?.isEditing ? "text" : "pointer" }}
                                    >
                                        <Typography variant="caption" color="text.secondary">
                                            {metric.label}
                                        </Typography>
                                        {metricState?.isEditing ? (
                                            <TextField
                                                size="small"
                                                autoFocus
                                                value={metricState.value}
                                                onChange={(event) => onMetricChange(metric.key, event.target.value)}
                                                onKeyDown={(event) => onMetricKeyDown(metric.key, event)}
                                                onBlur={() => onMetricBlur(metric.key)}
                                                inputProps={{ sx: { textAlign: "center" } }}
                                            />
                                        ) : (
                                            <Typography fontWeight={600}>
                                                {formatNumber(metric.value, metric.fractionDigits)}
                                            </Typography>
                                        )}
                                    </Grid>
                                )
                            })}
                        </Grid>
                        <Divider />
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            {featureChips.length > 0 ? (
                                featureChips.map((label) => (
                                    <Chip
                                        key={label}
                                        label={label}
                                        size="small"
                                        sx={{
                                            backgroundColor: "action.hover",
                                            color: "text.primary",
                                        }}
                                    />
                                ))
                            ) : (
                                <Typography variant="caption" color="text.secondary">
                                    <Trans id="asset-details.features.empty">Nessuna caratteristica disponibile.</Trans>
                                </Typography>
                            )}
                        </Stack>
                        <Divider />
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between">
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Avatar src={landlordLogo} sx={{ width: 36, height: 36 }}>
                                    {landlordName !== "-" ? landlordName.charAt(0) : "L"}
                                </Avatar>
                                <Box>
                                    <Typography fontWeight={600}>{landlordName}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        <Trans id="asset-details.landlord">Landlord</Trans>
                                    </Typography>
                                </Box>
                            </Stack>
                            <Stack direction="row" spacing={1}>
                                {asset?.landlord && asset.landlord.id !== "-1" && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={onOpenLandlordModal}
                                        sx={{ textTransform: "none" }}
                                    >
                                        <Trans id="asset-details.landlord.info">Info Landlord</Trans>
                                    </Button>
                                )}
                                <Button
                                    variant={canAssociateLandlord ? "contained" : "outlined"}
                                    size="small"
                                    color="secondary"
                                    startIcon={<PersonAddAltIcon fontSize="small" />}
                                    onClick={onAssociateLandlord}
                                    sx={{ textTransform: "none" }}
                                >
                                    {canAssociateLandlord ? (
                                        <Trans id="asset-details.landlord.associate">Associa Landlord</Trans>
                                    ) : (
                                        <Trans id="asset-details.landlord.associate-new">Associa nuovo Landlord</Trans>
                                    )}
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    )
}
